/**
 * Text Input Component
 */

import React, { FC, useEffect, useRef, useState } from 'react';
import { TextInputProps, TextInputTypes, TextInputColors, IconWrapperProps, CloseIconProps } from './textInput.types';
import { styledTextInput, styledIcons, styledButtonIcons } from './textInput.style';
import { withTheme } from 'styled-components';
import Icon from '../Icon/icon';
import Handler from '../../commonTypes/handler.types';

/**
 * styled componnts wrappers need to be outside the text input componnt 
 * they should not get remounted everytime props get updated.
 */

const TextInputWrapper = styledTextInput;
const IconWrapper = styledIcons;
const CloseIconWrapper = styledButtonIcons;
const getInputClassName: Function = (hasRightIcon: boolean, hasLeftIcon: boolean) => {
  let temp = [];

  if (hasRightIcon) {
    temp.push('right');
  }

  if (hasLeftIcon) {
    temp.push('left');
  }

  return temp.join(' ');
};

const TextInput: FC<TextInputProps> = ({
    placeHolder,
    className,
    type = TextInputTypes.PRIMARY,
    theme = {},
    ariaLabel,
    ariaLabelReset,
    handlers = {},
    resetHandlers = {},
    value,
    refElement: inputRef,
    cancelButton = false,
    required = false,
    iconType = '',
    iconColor,
    inputType = 'text',
    cancelButtonRef,
    additionalProps = {},
    spellCheck
  }: TextInputProps) => {
  const inputElRef: any = inputRef || useRef<HTMLInputElement>(null);
  const themeType: string = theme.dark ? 'dark' : type;
  const [ cancelBtnVisibility, setCancelBtnVisibility ] = useState(false);

  useEffect(() => {

    setCancelBtnVisibility(value !== undefined && value !== '');

  }, [value, setCancelBtnVisibility]);

  const onClearInput: Function = () => {
    if(inputElRef && inputElRef.current) {
      inputElRef.current.value = '';
      inputElRef.current.focus();
      setCancelBtnVisibility(false);
    }

  };

  const onValueChange: Function = () => {
    setCancelBtnVisibility(inputElRef.current.value !== '');
  };

  /// set default reset event handlers  
  const defaultInputHandlers: Handler = {};
  const defaultResetHandlers: Handler = {};

  
  if (cancelButton) {
    defaultResetHandlers['onClick'] = onClearInput;
    defaultInputHandlers['onChange'] = onValueChange;
  }

  // add default reset event handlers to merged list
  const completeResetButtonProps: object = { ...defaultResetHandlers };
  const completeInputHandlersProps: object = { ...defaultInputHandlers };
  
  
  Object.keys(resetHandlers).forEach((key: string) => {
    completeResetButtonProps[key] = (e: Event) => {
      if (defaultResetHandlers[key]) {
        defaultResetHandlers[key](e);
      }
      resetHandlers[key](e);
    };
  });
  
  Object.keys(handlers).forEach((key: string) => {
    completeInputHandlersProps[key] = (e: Event) => {
      if (defaultInputHandlers[key]) {
        defaultInputHandlers[key](e);
      }
      handlers[key](e);
    };
  });
  
  const CloseIconWrapperProps: CloseIconProps = {
    type: 'reset',
    'aria-label': ariaLabelReset || 'Reset',
    className: 'right',
    theme: themeType,
    ref: cancelButtonRef,
    ...completeResetButtonProps
  } 
  const mergedInputProps = {...completeInputHandlersProps, ...additionalProps};
  const completeTextInputProps: object = {
    type: inputType,
    placeholder: placeHolder,
    className: getInputClassName(cancelButton, iconType),
    'aria-label': ariaLabel || placeHolder || '',
    ref: inputElRef,
    required,
    value,
    spellCheck,
    ...mergedInputProps
  };
  
  const IconWrapperProps: IconWrapperProps = {
    className: 'left'
  }

  return (
    <TextInputWrapper theme={themeType} className={className}>
        { (iconType !== '') && <IconWrapper {...IconWrapperProps}>
          <Icon name={iconType} width={15} fill={iconColor || TextInputColors[themeType].icon}/>
        </IconWrapper> }
        <input {...completeTextInputProps} />
        { (cancelButton && value !== undefined && value !== '' && cancelBtnVisibility) && <CloseIconWrapper {...CloseIconWrapperProps}>
          <Icon
            name='close'
            width={13} 
          />
        </CloseIconWrapper> }
    </TextInputWrapper>
  );
};

export default withTheme(TextInput);